import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Fade } from "react-reveal";
import { message, Tooltip, Empty, Progress, Select, DatePicker, Tag, Avatar, Badge, Spin, Modal, Popconfirm, Popover } from "antd";
import { FaPlus, FaUserPlus, FaRegEdit, FaRegFolderOpen, FaFilter, FaSearch, FaChevronDown, FaTimes, FaPen, FaUser, FaClipboardList } from "react-icons/fa";
import moment from "moment";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import DataParser from "../../../utlits/Dataparser";
import CrmLeadsServices from "../../../Services/CrmLeadsServices";
import LeadPipelineService from "../../../Services/LeadPipelineService";
import UserProfile from "../../Reuseable/UserProfile..jsx";
import CreateCrmLead from "./CreateCrmLead";
import SocketService from "../../../Services/SocketService";
import ProspectAmountServices from "../../../Services/ProspectAmountServices";
import LeadStatusServices from "../../../Services/LeadStatusServices";
import DateField from "../../Reuseable/DateField";
import AssigneField from "../../Reuseable/AssigneField";
import LeadPendingField from "../../Reuseable/LeadPendingField";
import LeadComments from "./LeadComments";
import UserServices from "../../../Services/UserService";
import { FaRegComments } from "react-icons/fa";

const { Option } = Select;

const STAGE_CONFIG = {
    new: { label: "New", color: "#6366f1", bg: "#ede9fe" },
    contacted: { label: "Contacted", color: "#3b82f6", bg: "#dbeafe" },
    qualified: { label: "Qualified", color: "#f59e0b", bg: "#fef3c7" },
    proposal: { label: "Proposal", color: "#8b5cf6", bg: "#f3e8ff" },
    won: { label: "Won", color: "#10b981", bg: "#d1fae5" },
    lost: { label: "Lost", color: "#ef4444", bg: "#fee2e2" },
};

const truncateWords = (text, count) => {
    if (!text) return "";
    const str = String(text);
    const words = str.split(" ");

    // 1. Truncate by words
    let result = words.length <= count ? str : words.slice(0, count).join(" ") + "...";

    // 2. Safety: If any single word (or the result) is still excessively long, truncate by characters
    const CHAR_LIMIT = 30;
    if (result.length > CHAR_LIMIT) {
        return result.substring(0, CHAR_LIMIT) + "...";
    }

    return result;
};

const CrmLeads = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [hasAutoOpened, setHasAutoOpened] = useState(false);
    const [leads, setLeads] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [statusFilter, setStatusFilter] = useState([]);
    const [stageFilter, setStageFilter] = useState([]);
    const [leadTypeFilter, setLeadTypeFilter] = useState([]);
    const [dateRange, setDateRange] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editLeadId, setEditLeadId] = useState(null);
    const [reopenModal, setReopenModal] = useState(false);
    const [reopenTarget, setReopenTarget] = useState(null);
    const [reopenLoading, setReopenLoading] = useState(false);
    const [amountRanges, setAmountRanges] = useState([]);
    const [dynamicStatuses, setDynamicStatuses] = useState([]);
    const [allInitialData, setAllInitialData] = useState({ users: [], departments: [], sources: [] });
    const [activeTimeFilter, setActiveTimeFilter] = useState("stage_new");
    const [ownerFilter, setOwnerFilter] = useState([]);
    const [leadSourceFilter, setLeadSourceFilter] = useState([]);
    const [openCommentLeadId, setOpenCommentLeadId] = useState(null);
    const commentLeadIdRef = useRef(null);
    const [commentLeadNotes, setCommentLeadNotes] = useState([]);
    const [mentionUsers, setMentionUsers] = useState([]);
    const [visibleLeadsCount, setVisibleLeadsCount] = useState(20);
    const [isLazyLoading, setIsLazyLoading] = useState(false);
    const [isBackgroundLoading, setIsBackgroundLoading] = useState(true);

    const COL = { contact: '110px', phone: '110px', created: '105px', owner: '120px', type: '85px', date: '105px', source: '85px', stage: '95px', actions: '95px' };
    const headSt = { fontSize: '13px', fontWeight: '600', color: '#212529', textAlign: 'left', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

    const companyId = DataParser.CompanyId();
    const userPermissions = DataParser.CompanyUserPermissions();
    const companyIdentifier = DataParser.CompanyIdentifier();
    const currentUserId = DataParser.UserId();

    const fetchLeads = (isRefresh = false) => {
        if (!isRefresh) setIsLoading(true);

        // 1. PHASE 1: Fetch New Leads & Metadata (High Priority)
        CrmLeadsServices.getLeads(companyId, { scope: 'stage_new' })
            .then((res) => {
                if (res.Good) {
                    setLeads(res.data || []);

                    // Hydrate Metadata from the same response to reduce total requests
                    if (res.metadata) {
                        setAmountRanges(res.metadata.prospectAmounts || []);
                        setDynamicStatuses(res.metadata.statuses || []);
                        setAllInitialData({
                            users: res.metadata.users || [],
                            sources: res.metadata.sources || [],
                            relatedTo: res.metadata.relatedTo || []
                        });
                    }
                }
            })
            .catch((err) => console.error("Error fetching new leads:", err))
            .finally(() => {
                if (!isRefresh) setIsLoading(false);

                // 2. PHASE 2: Fetch Remaining Leads (Background)
                setIsBackgroundLoading(true);
                CrmLeadsServices.getLeads(companyId, { scope: 'others' })
                    .then((res) => {
                        // Phase 2 Metadata update (Full user directory for dropdowns)
                        if (res.metadata && res.metadata.users) {
                            setAllInitialData(prev => ({
                                ...prev,
                                users: res.metadata.users
                            }));
                        }

                        if (res.Good && res.data) {
                            setLeads(prev => {
                                const existingIds = new Set(prev.map(l => String(l.id)));
                                const uniqueOthers = res.data.filter(l => !existingIds.has(String(l.id)));
                                return [...prev, ...uniqueOthers];
                            });
                        }
                    })
                    .catch((err) => console.error("Error fetching other leads:", err))
                    .finally(() => setIsBackgroundLoading(false));
            });

        // 3. Fetch Mention Users for Comments (Independent)
        UserServices.getCrmLeadMentionUsers(companyId).then((res) => {
            if (res.Good && res.data && res.data.user) {
                const formatted = res.data.user.map(u => ({
                    id: u.id,
                    display: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
                    image: u.image
                }));
                setMentionUsers(formatted);
            }
        });
    };

    useEffect(() => {
        fetchLeads();

        // 🟢 Socket Integration for real-time updates
        SocketService.connect();
        const handleLeadUpdate = (data) => {
            if (String(data.company_id) !== String(companyId)) return;

            if (data.action === "activity_logged") {
                const leadId = data.data?.lead_id;
                if (leadId) {
                    setLeads(prev => prev.map(l =>
                        l.id === Number(leadId)
                            ? { ...l, last_activity_at: new Date().toISOString() }
                            : l
                    ));
                }
                return;
            }

            if (data.action === "create" && data.data) {
                setLeads(prev => {
                    if (prev.some(l => String(l.id) === String(data.data.id))) return prev;
                    return [data.data, ...prev];
                });
                return;
            }

            if ((data.action === "update" || data.action === "stage_changed") && data.data) {
                setLeads(prev => prev.map(l =>
                    String(l.id) === String(data.data.id) ? { ...l, ...data.data } : l
                ));
                return;
            }

            if (data.action === "delete" && data.data) {
                const deletedId = data.data.id || data.data.lead_id;
                if (deletedId) {
                    setLeads(prev => prev.filter(l => String(l.id) !== String(deletedId)));
                }
                return;
            }

            fetchLeads(true);
        };

        SocketService.on("lead_update", handleLeadUpdate);

        const handleNoteUpdate = (payload) => {
            if (Number(payload.company_id) !== Number(companyId)) return;

            // Update main leads list locally to avoid global re-fetch and loading spinners
            if (payload.action === "add_note" || payload.action === "update_note" || payload.action === "delete_note" || payload.action === "reaction_update") {
                setLeads(prevLeads => prevLeads.map(lead => {
                    const leadIdMatch = payload.data.lead_id || payload.data.note?.lead_id;
                    if (String(lead.id) === String(leadIdMatch)) {
                        let updatedNotes = lead.lead_notes ? [...lead.lead_notes] : [];

                        if (payload.action === "add_note") {
                            const noteId = payload.data.id || payload.data.note?.id;
                            const realNote = payload.data.note || payload.data;
                            updatedNotes = updatedNotes.filter(n => !(n.isOptimistic && String(n.user_id) === String(realNote.user_id)));
                            if (!updatedNotes.some(n => String(n.id) === String(noteId))) {
                                updatedNotes.push(realNote);
                            }
                        } else if (payload.action === "update_note" || payload.action === "reaction_update") {
                            updatedNotes = updatedNotes.map(n => String(n.id) === String(payload.data.id) ? payload.data : n);
                        } else if (payload.action === "delete_note") {
                            updatedNotes = updatedNotes.filter(n => String(n.id) !== String(payload.data.id));
                        }

                        return { ...lead, lead_notes: updatedNotes };
                    }
                    return lead;
                }));
            }

            // If comments modal is open for this lead, update its local notes
            if (payload.action === "add_note" || payload.action === "update_note" || payload.action === "delete_note") {
                setCommentLeadNotes(prev => {
                    const leadIdMatch = payload.data.lead_id || payload.data.note?.lead_id;
                    if (String(leadIdMatch) !== String(commentLeadIdRef.current)) return prev;

                    if (payload.action === "add_note") {
                        const noteId = payload.data.id || payload.data.note?.id;
                        const realNote = payload.data.note || payload.data;
                        // Remove the optimistic placeholder (fake Date.now() id) from this user
                        // so the real note from the socket doesn't appear alongside it
                        const optimisticMatch = prev.find(n => n.isOptimistic && String(n.user_id) === String(realNote.user_id));
                        const withoutOptimistic = prev.filter(n => !(n.isOptimistic && String(n.user_id) === String(realNote.user_id)));
                        // Preserve reply_to from the optimistic note if the server didn't return it
                        const noteToAdd = (optimisticMatch?.reply_to && !realNote.reply_to)
                            ? { ...realNote, reply_to: optimisticMatch.reply_to }
                            : realNote;
                        if (withoutOptimistic.some(n => String(n.id) === String(noteId))) return withoutOptimistic;
                        return [...withoutOptimistic, noteToAdd];
                    } else if (payload.action === "update_note") {
                        return prev.map(n => n.id === payload.data.id ? payload.data : n);
                    } else if (payload.action === "delete_note") {
                        return prev.filter(n => n.id !== payload.data.id);
                    }
                    return prev;
                });
            }
        };
        SocketService.on("lead_note_update", handleNoteUpdate);

        return () => {
            SocketService.off("lead_update", handleLeadUpdate);
            SocketService.off("lead_note_update", handleNoteUpdate);
        };
    }, [companyId]);

    useEffect(() => {
        if (openCommentLeadId) {
            setLeads(prev => prev.map(l => {
                const targetId = l.isHistoricalRow ? l.originalId : l.id;
                if (String(targetId) === String(openCommentLeadId)) {
                    return { ...l, lead_notes: commentLeadNotes };
                }
                return l;
            }));
        }
    }, [commentLeadNotes, openCommentLeadId]);

    const filteredLeads = useMemo(() => {
        const leadArray = Array.isArray(leads) ? leads : [];

        // Pre-compute once before the filter loop (avoid per-lead recalculation)
        const userId = DataParser.UserId();
        const userIdStr = String(userId);
        const mentionStr = `(${userId})`;
        const sVal = (searchValue || "").toLowerCase().trim();
        const dateStart = (dateRange?.[0] && dateRange?.[1]) ? dateRange[0].clone().startOf('day') : null;
        const dateEnd = (dateRange?.[0] && dateRange?.[1]) ? dateRange[1].clone().endOf('day') : null;
        // O(1) source lookup instead of O(sources) per lead per source filter entry
        const sourceNameMap = leadSourceFilter.length > 0
            ? new Map((allInitialData.sources || []).map(s => [String(s.id || s._id), (s.source || s.name || "").toLowerCase()]))
            : null;
        const stageCardMap = { stage_new: 'new', stage_contacted: 'contacted', stage_qualified: 'qualified', stage_proposal: 'proposal' };
        const isStageCard = activeTimeFilter in stageCardMap;
        const today = moment().startOf('day');

        const filtered = leadArray.filter(lead => {
            if (!lead) return false;

            // 1. Search Logic
            const matchesSearch = !sVal ||
                (lead.title || "").toLowerCase().includes(sVal) ||
                (lead.contact_name || "").toLowerCase().includes(sVal) ||
                (lead.contact_phone || "").toLowerCase().includes(sVal);

            // 2. Status Logic (Multi-select)
            const matchesStatus = statusFilter.length === 0 ||
                statusFilter.some(s => String(lead.lead_status || "").toLowerCase() === String(s).toLowerCase());

            // 2b. Stage Logic (Multi-select)
            const matchesStage = stageFilter.length === 0 ||
                stageFilter.some(s => String(lead.stage || "new") === String(s));

            // 3. Lead Owner Logic (Multi-select)
            const matchesOwner = ownerFilter.length === 0 ||
                ownerFilter.some(o => String(lead.lead_owner || "") === String(o));

            // 4. Lead Type Logic (Multi-select)
            const matchesLeadType = leadTypeFilter.length === 0 ||
                leadTypeFilter.some(lt => String(lead.department_id || "") === String(lt));

            // 5. Lead Source Logic — O(1) map lookup instead of O(sources) .find() per lead
            const matchesLeadSource = leadSourceFilter.length === 0 ||
                leadSourceFilter.some(ls => {
                    const srcName = sourceNameMap?.get(String(ls)) || "";
                    const leadSourceValue = String(lead.lead_source || "").toLowerCase();
                    return leadSourceValue === String(ls).toLowerCase() || leadSourceValue === srcName;
                });

            // 6. Date Range Logic — pre-computed bounds, no clone() per lead
            let matchesDate = true;
            if (dateStart && dateEnd) {
                const leadDate = moment(lead.due_date).startOf('day');
                matchesDate = leadDate.isValid() && leadDate.isSameOrAfter(dateStart) && leadDate.isSameOrBefore(dateEnd);
            }

            // GLOBAL ISOLATION (HIDE REASSIGNED LEADS)
            const prevOwnerIds = Array.isArray(lead.previous_owners) ? lead.previous_owners.map(String) : [];
            const currentOwnerId = String(typeof lead.lead_owner === 'object' ? (lead.lead_owner?.id || lead.lead_owner?._id) : lead.lead_owner);
            const creatorId = String(typeof lead.created_by === 'object' ? (lead.created_by?.id || lead.created_by?._id) : lead.created_by);

            const isMentioned = (lead.lead_notes || []).some(note =>
                (note.notes || "").includes(mentionStr) || (note.content || "").includes(mentionStr)
            );
            const isPrevOwnerOnly = prevOwnerIds.includes(userIdStr) && currentOwnerId !== userIdStr && creatorId !== userIdStr;
            if (isPrevOwnerOnly && activeTimeFilter !== "previously_assigned" && !isMentioned) return false;

            // ISOLATE WON/LOST + STAGE CARDS
            const isWon = lead.stage === 'won';
            const isLost = lead.stage === 'lost';
            if ((isWon || isLost) && activeTimeFilter !== 'won' && activeTimeFilter !== 'lost' && !isStageCard) return false;
            if (activeTimeFilter === 'won' && !isWon) return false;
            if (activeTimeFilter === 'lost' && !isLost) return false;
            if (isStageCard && lead.stage !== stageCardMap[activeTimeFilter]) return false;

            if (activeTimeFilter === "all") return matchesSearch && matchesStatus && matchesStage && matchesOwner && matchesLeadType && matchesLeadSource && matchesDate;

            let matchesTimeFilter = false;
            const leadStatus = String(lead.lead_status || '').toLowerCase();

            switch (activeTimeFilter) {
                case 'due_today':
                    matchesTimeFilter = lead.due_date && moment(lead.due_date).isSame(today, 'day');
                    break;
                case 'due_in_7_days':
                    if (lead.due_date) {
                        const dueDate = moment(lead.due_date);
                        matchesTimeFilter = dueDate.isAfter(today) && dueDate.isSameOrBefore(today.clone().add(7, 'days'));
                    }
                    break;
                case 'delayed':
                    matchesTimeFilter = lead.due_date && moment(lead.due_date).isBefore(today) && !['won', 'lost', 'dead'].includes(leadStatus);
                    break;
                case 'created_by_me':
                    matchesTimeFilter = creatorId === userIdStr;
                    break;
                case 'assigned_to_me':
                    matchesTimeFilter = currentOwnerId === userIdStr;
                    break;
                case 'previously_assigned':
                    matchesTimeFilter = prevOwnerIds.length > 0;
                    break;
                case 'won':
                case 'lost':
                case 'stage_new':
                case 'stage_contacted':
                case 'stage_qualified':
                case 'stage_proposal':
                default:
                    matchesTimeFilter = true;
                    break;
            }

            return matchesSearch && matchesStatus && matchesStage && matchesOwner && matchesLeadType && matchesLeadSource && matchesDate && matchesTimeFilter;
        });

        // MULTI-ROW EXPANSION FOR HISTORY — O(1) user lookup with Map
        if (activeTimeFilter === 'previously_assigned') {
            const userMap = new Map((allInitialData.users || []).map(u => [String(u.id || u._id), u]));
            return filtered.flatMap(lead => {
                const prevOwners = Array.isArray(lead.previous_owners) ? lead.previous_owners : [];
                return prevOwners.map((ownerId, index) => ({
                    ...lead,
                    id: `${lead.id}-hist-${index}`,
                    originalId: lead.id,
                    display_owner: userMap.get(String(ownerId)) || { id: ownerId, full_name: `User ${ownerId}` },
                    isHistoricalRow: true
                }));
            });
        }

        return filtered;
    }, [leads, searchValue, statusFilter, stageFilter, leadTypeFilter, leadSourceFilter, ownerFilter, dateRange, activeTimeFilter, allInitialData.users, allInitialData.sources]);

    const handleFilterChange = (setter) => (newValues) => {
        setter(newValues || []);
    };

    // Pre-compute all chip counts in a single pass — replaces 9 separate full-list filter runs in JSX
    const chipCounts = useMemo(() => {
        const userId = String(DataParser.UserId());
        const sVal = (searchValue || "").toLowerCase();
        const dateStart = (dateRange?.[0] && dateRange?.[1]) ? dateRange[0].clone().startOf('day') : null;
        const dateEnd = (dateRange?.[0] && dateRange?.[1]) ? dateRange[1].clone().endOf('day') : null;
        const sourceNameMap = leadSourceFilter.length > 0
            ? new Map((allInitialData.sources || []).map(s => [String(s.id || s._id), (s.source || s.name || "").toLowerCase()]))
            : null;
        const stageCardMap = { stage_new: 'new', stage_contacted: 'contacted', stage_qualified: 'qualified', stage_proposal: 'proposal' };

        const checkDropdowns = (l) => {
            if (sVal && !(l.title || "").toLowerCase().includes(sVal) && !(l.contact_name || "").toLowerCase().includes(sVal) && !(l.contact_phone || "").toLowerCase().includes(sVal)) return false;
            if (statusFilter.length && !statusFilter.some(s => String(l.lead_status || "").toLowerCase() === String(s).toLowerCase())) return false;
            if (stageFilter.length && !stageFilter.some(s => String(l.stage || "new") === String(s))) return false;
            if (ownerFilter.length && !ownerFilter.some(o => String(l.lead_owner || "") === String(o))) return false;
            if (leadTypeFilter.length && !leadTypeFilter.some(lt => String(l.department_id || "") === String(lt))) return false;
            if (leadSourceFilter.length && !leadSourceFilter.some(ls => {
                const srcName = sourceNameMap?.get(String(ls)) || "";
                return String(l.lead_source || "").toLowerCase() === String(ls).toLowerCase() || String(l.lead_source || "").toLowerCase() === srcName;
            })) return false;
            if (dateStart && dateEnd) {
                const ld = moment(l.due_date).startOf('day');
                if (!ld.isValid() || ld.isBefore(dateStart) || ld.isAfter(dateEnd)) return false;
            }
            return true;
        };

        const counts = { all: 0, created_by_me: 0, assigned_to_me: 0, stage_new: 0, stage_contacted: 0, stage_qualified: 0, stage_proposal: 0, won: 0, lost: 0 };

        (leads || []).forEach(l => {
            const cOid = String(typeof l.lead_owner === 'object' ? (l.lead_owner?.id || l.lead_owner?._id) : l.lead_owner);
            const cId = String(typeof l.created_by === 'object' ? (l.created_by?.id || l.created_by?._id) : l.created_by);
            const pOid = Array.isArray(l.previous_owners) ? l.previous_owners.map(String) : [];
            const isPrevOnly = pOid.includes(userId) && cOid !== userId && cId !== userId;
            const isWon = l.stage === 'won';
            const isLost = l.stage === 'lost';

            // won/lost chips (excluded from all other chips)
            if (isWon) {
                if (!isPrevOnly && (activeTimeFilter !== 'won' || checkDropdowns(l))) counts.won++;
                return;
            }
            if (isLost) {
                if (!isPrevOnly && (activeTimeFilter !== 'lost' || checkDropdowns(l))) counts.lost++;
                return;
            }

            if (isPrevOnly) return; // prev-only excluded from all non-terminal chips

            // stage chips
            const stageKey = Object.entries(stageCardMap).find(([, s]) => s === l.stage)?.[0];
            if (stageKey && (activeTimeFilter !== stageKey || checkDropdowns(l))) counts[stageKey]++;

            // all chip
            if (activeTimeFilter !== 'all' || checkDropdowns(l)) counts.all++;

            // me chips
            if (cId === userId && (activeTimeFilter !== 'created_by_me' || checkDropdowns(l))) counts.created_by_me++;
            if (cOid === userId && (activeTimeFilter !== 'assigned_to_me' || checkDropdowns(l))) counts.assigned_to_me++;
        });

        return counts;
    }, [leads, searchValue, statusFilter, stageFilter, ownerFilter, leadTypeFilter, leadSourceFilter, dateRange, activeTimeFilter, allInitialData.sources]);

    // 🟢 Intersection Observer for Infinite Scroll
    useEffect(() => {
        const totalFiltered = filteredLeads.length;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && !isLazyLoading && totalFiltered > visibleLeadsCount) {
                setIsLazyLoading(true);
                setTimeout(() => {
                    setVisibleLeadsCount(prev => prev + 20);
                    setIsLazyLoading(false);
                }, 800);
            }
        }, { threshold: 0.1 });

        const sentinel = document.getElementById("leads-list-sentinel");
        if (sentinel) observer.observe(sentinel);

        return () => observer.disconnect();
    }, [isLazyLoading, visibleLeadsCount, filteredLeads.length]);

    // 🟠 Handle Auto-opening Modal from Notifications
    useEffect(() => {
        if (!isLoading) {
            const stateLeadId = location.state?.LeadId;
            const queryLeadId = searchParams.get("leadId");
            const targetId = stateLeadId || queryLeadId;

            if (targetId) {
                // Always try to open if a targetId is present (e.g. from notification click)
                // The state/param clearing below ensures we don't loop.
                handleEdit(targetId);

                // Clear state and query params immediately to prevent recursive opening
                if (stateLeadId) {
                    navigate(location.pathname, { replace: true, state: {} });
                }
                if (queryLeadId) {
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete("leadId");
                    setSearchParams(newParams, { replace: true });
                }
                setHasAutoOpened(true);
            } else if (!hasAutoOpened) {
                // If no specific target, just mark as checked for normal initial load
                setHasAutoOpened(true);
            }
        }
    }, [isLoading, hasAutoOpened, location.pathname, searchParams, navigate, location.state]);

    // Lock body scroll when modal is open — same as Bootstrap modal behaviour.
    // scrollbar-gutter: stable on body means the gutter space is always reserved,
    // so hiding overflow never causes a layout shift in the table columns.
    useEffect(() => {
        if (isModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isModalOpen]);

    const handleLocalUpdate = (leadId, newData) => {
        setLeads(prev => prev.map(l => {
            if (l.id === leadId) {
                const updatedLead = { ...l, ...newData };
                // If owner ID was updated, also update the 'owner' object for display parity
                if (newData.owner || newData.lead_owner) {
                    const ownerId = newData.owner || newData.lead_owner;
                    const fullOwner = (allInitialData.users || []).find(u => String(u.id || u._id) === String(ownerId));
                    if (fullOwner) {
                        updatedLead.owner = fullOwner;
                        updatedLead.lead_owner = ownerId;
                    }
                }
                return updatedLead;
            }
            return l;
        }));
    };

    const updateLeadField = (id, data) => {
        // Map common field names for compatibility
        const body = { ...data };
        if (body.duedate) { body.due_date = body.duedate; delete body.duedate; }
        if (body.assignee) { body.lead_owner = body.assignee; delete body.assignee; }

        CrmLeadsServices.updateLead(id, body)
            .then(res => {
                if (res.Good) {
                    message.success("Lead updated successfully");
                    // Socket will handle the refresh, but handleLocalUpdate provides instant feel
                    handleLocalUpdate(id, res.data);
                } else {
                    message.error(res.data || "Failed to update lead");
                }
            })
            .catch(err => {
                console.error("Update error:", err);
                message.error("An error occurred while updating the lead");
            });
    };

    const getStatusColor = (status) => {
        // Global message config to ensure alerts appear on top of modals
        message.config({
            top: 50,
            duration: 3,
            maxCount: 3,
            rtl: false,
            zIndex: 100010,
            getContainer: () => document.body,
        });

        // 1. Look for color in dynamic statuses (from backend)
        const dynamicMatch = dynamicStatuses.find(s => String(s.name).toLowerCase() === String(status).toLowerCase());
        if (dynamicMatch && dynamicMatch.color) return dynamicMatch.color;

        // 2. Fallback to local mapping (for common statuses)
        switch (String(status).toLowerCase()) {
            case "won": return "#10b981"; // Emerald
            case "lost": return "#ef4444"; // Rose
            case "pending": return "#f59e0b"; // Amber
            case "in discussion": return "#3b82f6"; // Blue
            case "potential": return "#8b5cf6"; // Violet
            case "dead": return "#6b7280"; // Gray
            case "open": return "#0DDFAB"; // Default Planit Teal
            default: return "#94a3b8";
        }
    };

    const handleEdit = (id) => {
        setEditLeadId(id);
        setIsModalOpen(true);
    };

    const handleReopen = (lead) => {
        setReopenTarget(lead);
        setReopenModal(true);
    };

    const handleReopenConfirm = async () => {
        setReopenLoading(true);
        try {
            await LeadPipelineService.reopenLead(reopenTarget.id, {
                company_id: companyId,
                target_stage: "new",
            });
            setLeads(prev => prev.map(l =>
                l.id === reopenTarget.id ? { ...l, stage: "new" } : l
            ));
            message.success("Lead re-opened and moved to New.");
            setReopenModal(false);
        } catch (err) {
            message.error(err?.response?.data?.data || err?.response?.data?.message || "Failed to re-open lead.");
        } finally {
            setReopenLoading(false);
        }
    };

    const handleOpenComments = (lead) => {
        setOpenCommentLeadId(lead.id);
        commentLeadIdRef.current = lead.id;
        setCommentLeadNotes(lead.lead_notes || []);
    };

    const handleCreate = () => {
        setEditLeadId(null);
        setIsModalOpen(true);
    };

    const getCurrencyByRange = (amount) => {
        if (!amount || amount === "-") return "";
        // Match range string (normalize whitespace/dashes)
        const matched = amountRanges.find(r =>
            `${r.min_amount}-${r.max_amount}` === amount.replace(/\s+/g, '') ||
            `${r.min_amount} - ${r.max_amount}` === amount
        );
        return matched ? ` ${matched.currency}` : "";
    };

    const handleShowMore = () => {
        setVisibleLeadsCount(prev => prev + 20);
    };

    const confirmDelete = (id) => {
        Modal.confirm({
            title: "Delete Confirmation",
            centered: true,
            className: "delete-modal",
            content: (
                <div style={{ marginTop: '12px' }}>
                    <p>Are you sure you want to delete this lead?</p>
                    <p style={{ color: '#ff4d4f', fontSize: '13px', marginTop: '8px' }}>
                        <i className="uil uil-exclamation-triangle me-1" />
                        This action cannot be undone.
                    </p>
                </div>
            ),
            okText: "Delete",
            cancelText: "Cancel",
            okType: 'danger',
            okButtonProps: {
                style: { borderRadius: '5px' },
                className: "btn-primary-custom"
            },
            cancelButtonProps: { style: { borderRadius: '5px' } },
            onOk: async () => {
                try {
                    const res = await CrmLeadsServices.deleteLead(id);
                    if (res.Good) {
                        message.success("Lead deleted successfully");
                        setLeads(prev => prev.filter(l => String(l.id) !== String(id)));
                    }
                } catch (err) {
                    message.error("Failed to delete lead");
                }
            },
        });
    };

    return (
        <>
            <style>{`
                .crm-lead-modal-overlay {
                    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
                    background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center;
                    z-index: 100005;
                }

                /* Leads filter dropdown option sizes */
                .leads-filter-dropdown .ant-select-item {
                    min-height: 26px !important;
                    padding: 3px 10px !important;
                }
                .leads-filter-dropdown .ant-select-item-option-content {
                    font-size: 11px !important;
                }
                @media (min-width: 1400px) {
                    .leads-filter-dropdown .ant-select-item-option-content {
                        font-size: 12px !important;
                    }
                }
            `}</style>
            <Helmet>
                <title>Leads Management | Planit</title>
            </Helmet>

            <div className="main-content">
                <div className="page-content" style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
                    <div className="container-fluid">
                        {!userPermissions.includes("leads-list") ? (
                            <div className="row px-5 mt-4">
                                <div className="col-12 text-center py-5 card shadow-sm" style={{ borderRadius: '15px' }}>
                                    <Empty
                                        description={
                                            <span className="text-muted fw-semibold">
                                                You do not have permission to view Lead Management.
                                            </span>
                                        }
                                    />
                                </div>
                            </div>
                        ) : (
                            <>
                                <style>{`
                                    /* DatePicker Underline & Active Bar */
                                    .ant-picker-range .ant-picker-active-bar { background: #1D1D1D !important; }
                                    .ant-picker-focused .ant-picker-suffix { color: #1D1D1D !important; }

                                    .task-stat-chip:hover {
                                        border-color: #0DDFAB !important;
                                    }

                                    /* Tasks Parity Styling */
                                    .task_heading_column {
                                        display: flex !important;
                                        align-items: center !important;
                                        width: 100% !important;
                                        min-height: 28px !important;
                                        height: 28px !important;
                                        padding: 0 !important;
                                        background-color: transparent !important;
                                        border-left: none !important;
                                    }
                                    .head-title {
                                        font-weight: 600;
                                        margin: 0;
                                        color: #fff;
                                        font-size: 13px !important;
                                        text-transform: capitalize;
                                        line-height: 28px !important;
                                        white-space: nowrap;
                                        padding: 0;
                                        height: 28px !important;
                                        display: block !important;
                                    }
                                    /* Table Modernization (TASKS PARITY) */
                                    .lead-header {
                                        display: flex;
                                        align-items: center;
                                        padding: 10px 0;
                                        background: #F2F2F2;
                                        border-radius: 0;
                                        border-bottom: 1px solid #e5e7eb;
                                    }
                                    .lead-row {
                                        display: flex;
                                        width: 100%;
                                        align-items: center;
                                        min-height: 44px;
                                        border-bottom: 1px solid #f3f4f6;
                                        padding: 4px 0;
                                        transition: background-color 0.15s;
                                        background: #fff;
                                    }
                                    .lead-row:hover { background-color: #f9fafb !important; }
                                    .priority-indicator {
                                        flex-shrink: 0;
                                        align-self: stretch;
                                        width: 4px;
                                        transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                                        display: flex;
                                        align-items: center;
                                        overflow: hidden;
                                        cursor: default;
                                        border-radius: 0 4px 4px 0;
                                    }
                                    .priority-indicator:hover {
                                        width: 50px;
                                        box-shadow: 2px 0 8px rgba(0,0,0,0.1);
                                    }
                                    .stage-badge {
                                        border-radius: 5px !important;
                                    }
                                    .priority-indicator span {
                                        opacity: 0;
                                        transition: opacity 0.2s ease;
                                        white-space: nowrap;
                                        font-size: 10px;
                                        font-weight: 700;
                                        color: #1d1d1d;
                                        padding-left: 6px;
                                    }
                                    .priority-indicator:hover span {
                                        opacity: 1;
                                        transition-delay: 0.1s;
                                    }

                                    /* BORDERLESS FOR TABLE LISTING ONLY */
                                    .lead-row .ant-select-selector,
                                    .lead-row .ant-picker,
                                    .lead-row .ant-select-focused:not(.ant-select-customize-input) .ant-select-selector {
                                        border: none !important;
                                        background: transparent !important;
                                        box-shadow: none !important;
                                        outline: none !important;
                                    }

                                    /* Search + Filters + Createbtn */
                                    .task-filters-row { display: flex; align-items: center; gap: 10px; flex-wrap: nowrap; margin-bottom: 12px; min-width: 0; border-top: 1px solid #F2F2F2; border-bottom: 1px solid #F2F2F2; padding: 10px 0; }
                                    .custom-filter.float-select-wrap { position: relative; flex: 0 1 140px; }
                                    .task-filters-row .custom-filter.float-select-wrap .ant-select-selector,
                                    .task-filters-row .custom-filter.float-select-wrap .ant-picker {
                                        height: 34px !important; min-height: 34px !important; border-radius: 6px !important;
                                        border: 1.5px solid #F2F2F2 !important;
                                    }
                                    .task-filters-row .custom-filter.float-select-wrap .ant-select-selector:hover,
                                    .task-filters-row .custom-filter.float-select-wrap .ant-select-focused .ant-select-selector { border-color: #1D1D1D !important; box-shadow: none !important; }
                                    .task-filters-row .custom-filter.float-select-wrap .ant-picker:hover,
                                    .task-filters-row .custom-filter.float-select-wrap .ant-picker-focused { border-color: #1D1D1D !important; box-shadow: none !important; }
                                    .task-filters-row .custom-filter.float-select-wrap .ant-select-clear { display: none !important; }
                                    .task-filters-row .custom-filter.float-select-wrap .ant-select-selector { display: flex !important; align-items: center !important; }
                                    .task-filters-row .custom-filter.float-select-wrap .ant-select-selection-overflow {
                                        display: flex !important; align-items: center !important;
                                        flex-wrap: nowrap !important; height: 100% !important; overflow: hidden !important;
                                    }
                                    .task-filters-row .custom-filter.float-select-wrap .ant-select-selection-overflow-item { display: flex !important; align-items: center !important; }
                                    .task-filters-row .custom-filter.float-select-wrap .ant-select-selection-item,
                                    .task-filters-row .custom-filter.float-select-wrap .ant-picker-input > input {
                                        line-height: 30px !important; font-size: 11px !important; font-weight: 300 !important; color: #000000d9 !important;
                                    }
                                    .task-filters-row .custom-filter.float-select-wrap .ant-select-selection-placeholder {
                                        line-height: 30px !important; font-size: 11px !important; font-weight: 300 !important; color: #D2D2D2 !important;
                                    }
                                    .task-filters-row .custom-filter.float-select-wrap label {
                                        position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
                                        font-size: 11px !important; font-weight: 300 !important; color: #D2D2D2; background: transparent;
                                        padding: 0 4px; z-index: 10; transition: all 0.2s ease; pointer-events: none;
                                    }
                                    .task-filters-row .custom-filter.float-select-wrap.has-value label,
                                    .task-filters-row .custom-filter.float-select-wrap:focus-within label {
                                        top: 0; transform: translateY(-50%); font-size: 11px !important;
                                        font-weight: 600 !important; color: #1D1D1D; background: #fff;
                                    }
                                    .task-filters-row .custom-filter.float-select-wrap:hover label { color: #1D1D1D !important; }
                                    .task-date-range-wrap { position: relative; }
                                    .task-date-range-wrap::before,
                                    .task-date-range-wrap::after {
                                        position: absolute; top: 0; transform: translateY(-50%);
                                        font-size: 11px !important; font-weight: 600 !important; color: #D2D2D2;
                                        background: #fff; padding: 0 3px; z-index: 10;
                                        pointer-events: none; opacity: 0; transition: color 0.2s, opacity 0.2s;
                                    }
                                    .task-date-range-wrap::before { content: 'Start Date'; left: 12px; }
                                    .task-date-range-wrap::after  { content: 'End Date';   left: calc(50% + 8px); }
                                    .task-date-range-wrap.has-value::before { opacity: 1; color: #1D1D1D !important; }
                                    .task-date-range-wrap.has-end-value::after  { opacity: 1; color: #1D1D1D !important; }
                                    .task-date-range-wrap:hover .ant-picker,
                                    .task-date-range-wrap .ant-picker-focused { border-color: #1D1D1D !important; box-shadow: none !important; }
                                    .task-date-range-wrap:hover .ant-picker-separator,
                                    .task-date-range-wrap:hover .ant-picker-range-separator { color: #D2D2D2 !important; }
                                    .task-date-range-wrap:hover .ant-picker-input > input,
                                    .task-date-range-wrap:hover .ant-picker-separator { color: #1D1D1D !important; }
                                    .task-date-range-wrap:hover .ant-picker-input > input::placeholder { color: #1D1D1D !important; opacity: 1 !important; }
                                    .task-filters-row .ant-picker-separator,
                                    .task-filters-row .ant-picker-range-separator { color: #D2D2D2 !important; }
                                    .task-search-wrap { position: relative; flex: 0 1 180px; }
                                    .task-search-wrap input {
                                        width: 100%; height: 34px; padding: 0 36px 0 14px;
                                        border: 1.5px solid #F2F2F2; border-radius: 5px;
                                        font-size: 11px !important; font-weight: 300 !important; outline: none; background: #fff;
                                    }
                                    .task-search-wrap input:focus { border-color: #1D1D1D !important; box-shadow: none !important; }
                                    .task-search-wrap label {
                                        position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
                                        font-size: 11px !important; font-weight: 300 !important; color: #D2D2D2; pointer-events: none;
                                        transition: all 0.2s ease; background: #fff; padding: 0 2px;
                                    }
                                    .task-search-wrap input:focus ~ label,
                                    .task-search-wrap input:not(:placeholder-shown) ~ label {
                                        top: 0; font-size: 11px !important; font-weight: 600 !important; color: #1D1D1D;
                                    }
                                    .task-search-wrap i {
                                        position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
                                        color: #D2D2D2; font-size: 16px; transition: all 0.2s ease;
                                    }
                                    .task-search-wrap input:focus ~ i {
                                        color: #1D1D1D !important;
                                    }
                                    .task-create-btn {
                                        height: 34px; border-radius: 5px; font-size: 13px; font-weight: 600;
                                        padding: 0 12px; display: flex; align-items: center; gap: 6px;
                                        background-color: #0DDFAB; border: 1px solid #0DDFAB; color: #fff;
                                        white-space: nowrap; cursor: pointer; text-decoration: none; flex-shrink: 0;
                                    }
                                    .task-create-btn:hover { background-color: #0bc698; border-color: #0bc698; color: #fff; }
                                    
                                    .card, .card-body {
                                        background-color: transparent !important;
                                        box-shadow: none !important;
                                        border: none !important;
                                    }
                                    .breadcrumb-item a, .breadcrumb-item.active {
                                        color: #404e67 !important;
                                    }

                                `}</style>


                                {/* Top Summary Cards (MATCHING TASKS MODULE) */}
                                {(() => {
                                    const CHIPS = [
                                        { key: 'all', label: 'Active', type: 'icon', IconComponent: FaClipboardList },
                                        { key: 'stage_new', label: 'New', type: 'svg', icon: '/assets/images/new.svg' },
                                        { key: 'stage_contacted', label: 'Contacted', type: 'svg', icon: '/assets/images/contacted.svg' },
                                        { key: 'stage_qualified', label: 'Qualified', type: 'svg', icon: '/assets/images/qualified.svg' },
                                        { key: 'stage_proposal', label: 'Proposal', type: 'svg', icon: '/assets/images/proposal.svg' },
                                        { key: 'won', label: 'Won', type: 'svg', icon: '/assets/images/won.svg' },
                                        { key: 'lost', label: 'Lost', type: 'svg', icon: '/assets/images/lost.svg' },
                                        { key: 'created_by_me', label: 'Created by me', type: 'icon', IconComponent: FaPen },
                                        { key: 'assigned_to_me', label: 'Assigned to me', type: 'icon', IconComponent: FaUser },
                                    ];
                                    return (
                                        <div className="px-3 mb-2" style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(9, 1fr)',
                                            gap: '10px'
                                        }}>
                                            {CHIPS.map((chip) => {
                                                const isActive = activeTimeFilter === chip.key;
                                                const count = chipCounts[chip.key] ?? 0;

                                                return (
                                                    <div
                                                        key={chip.key}
                                                        onClick={() => setActiveTimeFilter(chip.key)}
                                                        className="task-stat-chip"
                                                        style={{
                                                            background: '#fff',
                                                            border: isActive ? '1px solid #0DDFAB' : '1px solid #F2F2F2',
                                                            borderRadius: '10px',
                                                            padding: '6px 10px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'flex-start',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                                            transition: 'all 0.2s ease',
                                                            userSelect: 'none',
                                                            minWidth: 0
                                                        }}
                                                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor = '#0DDFAB'; }}
                                                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.borderColor = '#F2F2F2'; }}
                                                    >
                                                        <div style={{
                                                            width: '26px',
                                                            height: '26px',
                                                            borderRadius: '6px',
                                                            background: '#d1faf0',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            marginBottom: '2px',
                                                            flexShrink: 0
                                                        }}>
                                                            {chip.type === 'icon' ? (
                                                                <chip.IconComponent style={{ fontSize: '14px', color: '#0a9e7a' }} />
                                                            ) : (
                                                                <img
                                                                    src={chip.icon}
                                                                    alt=""
                                                                    style={{
                                                                        width: '15px',
                                                                        height: '15px',
                                                                        filter: 'brightness(0) saturate(100%) invert(43%) sepia(93%) saturate(394%) hue-rotate(118deg) brightness(91%) contrast(95%)'
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', width: '100%', gap: '4px' }}>
                                                            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                                                                {chip.label}
                                                            </span>
                                                            <span style={{ fontSize: '18px', fontWeight: '700', color: '#1a2332', lineHeight: 1, flexShrink: 0 }}>
                                                                {isLoading ? <Skeleton width={20} /> : String(count).padStart(2, '0')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}

                                <div className="row px-3 mb-2">
                                    <div className="col-12">
                                        <div className="task-filters-row px-0">
                                            <div className="task-search-wrap">
                                                <input
                                                    type="text"
                                                    id="leadSearchInput"
                                                    placeholder=" "
                                                    value={searchValue}
                                                    onChange={(e) => setSearchValue(e.target.value)}
                                                />
                                                <label htmlFor="leadSearchInput">Search Leads...</label>
                                                {searchValue
                                                    ? <i className="uil uil-times" onMouseDown={(e) => { e.preventDefault(); setSearchValue(''); }} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#D2D2D2', fontSize: '15px', cursor: 'pointer', zIndex: 20 }} />
                                                    : <i className="uil uil-search" />
                                                }
                                            </div>

                                            <div className={`float-select-wrap custom-filter ${leadSourceFilter.length > 0 ? 'has-value' : ''}`} style={{ flexShrink: 0, position: 'relative' }}>
                                                <label>Lead Source</label>
                                                <Select
                                                    mode="multiple"
                                                    dropdownClassName="leads-filter-dropdown"
                                                    placeholder=""
                                                    value={leadSourceFilter}
                                                    onChange={handleFilterChange(setLeadSourceFilter)}
                                                    allowClear
                                                    maxTagCount={0}
                                                    maxTagPlaceholder={(v) => v.length > 0 ? <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{v.length} Selected <span onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setLeadSourceFilter([]); }} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}><i className="uil-times" style={{ fontSize: '13px', color: '#999' }} /></span></div> : ""}
                                                    style={{ width: '100%' }}
                                                >
                                                    {allInitialData.sources?.map(s => (
                                                        <Option key={String(s.id || s._id)} value={String(s.id || s._id)}>{s.source || s.name}</Option>
                                                    ))}
                                                </Select>
                                                <FaChevronDown size={10} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#D2D2D2', pointerEvents: 'none', zIndex: 5 }} />
                                            </div>

                                            <div className={`float-select-wrap custom-filter ${leadTypeFilter.length > 0 ? 'has-value' : ''}`} style={{ flexShrink: 0, position: 'relative' }}>
                                                <label>Lead Interest</label>
                                                <Select
                                                    mode="multiple"
                                                    dropdownClassName="leads-filter-dropdown"
                                                    placeholder=""
                                                    value={leadTypeFilter}
                                                    onChange={handleFilterChange(setLeadTypeFilter)}
                                                    allowClear
                                                    maxTagCount={0}
                                                    maxTagPlaceholder={(v) => v.length > 0 ? <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{v.length} Selected <span onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setLeadTypeFilter([]); }} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}><i className="uil-times" style={{ fontSize: '13px', color: '#999' }} /></span></div> : ""}
                                                    style={{ width: '100%' }}
                                                >
                                                    {allInitialData.relatedTo?.map(d => (
                                                        <Option key={String(d.id)} value={String(d.id)}>{d.name}</Option>
                                                    ))}
                                                </Select>
                                                <FaChevronDown size={10} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#D2D2D2', pointerEvents: 'none', zIndex: 5 }} />
                                            </div>

                                            <div className={`float-select-wrap custom-filter ${stageFilter.length > 0 ? 'has-value' : ''}`} style={{ flexShrink: 0, position: 'relative' }}>
                                                <label>Stage</label>
                                                <Select
                                                    mode="multiple"
                                                    dropdownClassName="leads-filter-dropdown"
                                                    placeholder=""
                                                    value={stageFilter}
                                                    onChange={handleFilterChange(setStageFilter)}
                                                    allowClear
                                                    maxTagCount={0}
                                                    maxTagPlaceholder={(v) => v.length > 0 ? <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{v.length} Selected <span onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setStageFilter([]); }} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}><i className="uil-times" style={{ fontSize: '13px', color: '#999' }} /></span></div> : ""}
                                                    style={{ width: '100%' }}
                                                >
                                                    {Object.entries(STAGE_CONFIG).map(([key, cfg]) => (
                                                        <Option key={key} value={key}>
                                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.color, display: 'inline-block' }} />
                                                                {cfg.label}
                                                            </span>
                                                        </Option>
                                                    ))}
                                                </Select>
                                                <FaChevronDown size={10} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#D2D2D2', pointerEvents: 'none', zIndex: 5 }} />
                                            </div>

                                            <div className={`float-select-wrap custom-filter ${ownerFilter.length > 0 ? 'has-value' : ''}`} style={{ flexShrink: 0, position: 'relative' }}>
                                                <label>Lead Owner</label>
                                                <Select
                                                    mode="multiple"
                                                    dropdownClassName="leads-filter-dropdown"
                                                    placeholder=""
                                                    value={ownerFilter}
                                                    onChange={handleFilterChange(setOwnerFilter)}
                                                    allowClear
                                                    maxTagCount={0}
                                                    maxTagPlaceholder={(v) => v.length > 0 ? <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{v.length} Selected <span onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setOwnerFilter([]); }} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}><i className="uil-times" style={{ fontSize: '13px', color: '#999' }} /></span></div> : ""}
                                                    style={{ width: '100%' }}
                                                >
                                                    {allInitialData.users.map(u => (
                                                        <Option key={u.id} value={u.id}>{u.first_name} {u.last_name}</Option>
                                                    ))}
                                                </Select>
                                                <FaChevronDown size={10} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#D2D2D2', pointerEvents: 'none', zIndex: 5 }} />
                                            </div>

                                            <div className={`float-select-wrap custom-filter task-date-range-wrap ${dateRange?.[0] ? 'has-value' : ''} ${dateRange?.[1] ? 'has-end-value' : ''}`} style={{ flex: '0 1 215px' }}>
                                                <DatePicker.RangePicker
                                                    value={dateRange}
                                                    onChange={(dates) => setDateRange(dates)}
                                                    allowClear
                                                    separator="→"
                                                    placeholder={['Start Date', 'End Date']}
                                                    style={{ width: '100%' }}
                                                    suffixIcon={
                                                        <div style={{ backgroundColor: '#F4F4F4', borderRadius: '5px', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '-4px' }}>
                                                            <i className="uil-schedule" style={{ color: '#888', fontSize: '14px' }} />
                                                        </div>
                                                    }
                                                />
                                            </div>

                                            {userPermissions.includes("leads-create") && (
                                                <button className="task-create-btn ms-auto" onClick={handleCreate}>
                                                    <FaPlus size={12} /> Create Lead
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="row px-3">
                                    <div className="col-12">
                                        <div className="w-100 crm-leads-list-container" style={{ backgroundColor: 'transparent' }}>
                                            <div className="lead-header">
                                                <div className="lead-title-col" style={{ flex: 1, minWidth: 0, paddingLeft: '12px' }}><span style={headSt}>Lead Title</span></div>
                                                <div style={{ width: COL.contact, flexShrink: 0, padding: '0 8px' }}><span style={headSt}>Client Name</span></div>
                                                <div style={{ width: COL.phone, flexShrink: 0, padding: '0 8px' }}><span style={headSt}>Phone</span></div>
                                                <div style={{ width: COL.created, flexShrink: 0, padding: '0 8px' }}><span style={headSt}>Created</span></div>
                                                <div style={{ width: COL.owner, flexShrink: 0, padding: '0 8px' }}><span style={headSt}>Owner</span></div>
                                                <div style={{ width: COL.type, flexShrink: 0, padding: '0 8px' }}><span style={headSt}>Interest</span></div>
                                                <div style={{ width: COL.date, flexShrink: 0, padding: '0 8px' }}><span style={headSt}>FollowUp</span></div>
                                                <div style={{ width: COL.source, flexShrink: 0, padding: '0 8px' }}><span style={headSt}>Source</span></div>
                                                <div style={{ width: COL.stage, flexShrink: 0, padding: '0 6px' }}><span style={headSt}>Stage</span></div>
                                                <div style={{ width: COL.actions, flexShrink: 0, padding: '0 8px' }}><span style={headSt}>Action</span></div>
                                            </div>

                                            {isLoading ? (
                                                [1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                                                    <div key={i} className="lead-row">
                                                        <div className="lead-title-col" style={{ flex: 1, paddingLeft: '12px' }}><Skeleton width="90%" height={14} /></div>
                                                        <div style={{ width: COL.contact, padding: '0 8px' }}><Skeleton width="80%" height={12} /></div>
                                                        <div style={{ width: COL.phone, padding: '0 8px' }}><Skeleton width="80%" height={12} /></div>
                                                        <div style={{ width: COL.created, padding: '0 8px' }}><Skeleton width="70%" height={12} /></div>
                                                        <div style={{ width: COL.owner, padding: '0 8px' }}><Skeleton width="90%" height={12} /></div>
                                                        <div style={{ width: COL.type, padding: '0 8px' }}><Skeleton width="80%" height={12} /></div>
                                                        <div style={{ width: COL.date, padding: '0 8px' }}><Skeleton width="70%" height={12} /></div>
                                                        <div style={{ width: COL.source, padding: '0 8px' }}><Skeleton width="70%" height={12} /></div>
                                                        <div style={{ width: COL.stage, padding: '0 6px' }}><Skeleton width="80%" height={12} /></div>
                                                        <div style={{ width: COL.actions, padding: '0 8px' }}><Skeleton width="40%" height={12} /></div>
                                                    </div>
                                                ))
                                            ) : (
                                                filteredLeads.length > 0 ? (
                                                    <>
                                                        {filteredLeads.slice(0, visibleLeadsCount).map((lead) => {
                                                            const stageKey = lead.stage || 'new';
                                                            const cfg = STAGE_CONFIG[stageKey] || STAGE_CONFIG.new;
                                                            const userId = DataParser.UserId();
                                                            const canEdit = lead.can_edit_lead !== false && activeTimeFilter !== 'previously_assigned';

                                                            return (
                                                                <div className="lead-row" key={lead.id}>
                                                                    <div className="priority-indicator" style={{ backgroundColor: cfg.color }}>
                                                                        <span>{cfg.label}</span>
                                                                    </div>

                                                                    {/* Title Column (Flexible, fills space) */}
                                                                    <div className="lead-title-col" style={{ flex: 1, minWidth: 0, paddingLeft: '8px', paddingRight: '8px', overflow: 'hidden' }}>
                                                                        <div
                                                                            role="button"
                                                                            onClick={() => canEdit && handleEdit(lead.id)}
                                                                            style={{ cursor: 'pointer', overflow: 'hidden' }}
                                                                        >
                                                                            <span style={{ color: '#111827', fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                                                                                {lead.title}
                                                                            </span>
                                                                        </div>
                                                                    </div>

                                                                    {/* Contact */}
                                                                    <div style={{ width: COL.contact, flexShrink: 0, padding: '0 8px', overflow: 'hidden' }}>
                                                                        <span style={{ fontSize: '13px', color: '#1d1d1d', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                                                                            {lead.contact_name || "—"}
                                                                        </span>
                                                                    </div>

                                                                    {/* Phone */}
                                                                    <div style={{ width: COL.phone, flexShrink: 0, padding: '0 8px', overflow: 'hidden' }}>
                                                                        <span style={{ fontSize: '13px', color: '#1d1d1d', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                                                                            {lead.contact_phone || "—"}
                                                                        </span>
                                                                    </div>

                                                                    {/* Created */}
                                                                    <div style={{ width: COL.created, flexShrink: 0, padding: '0 8px', overflow: 'hidden' }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%', overflow: 'hidden' }}>
                                                                            <i className="uil uil-calendar-alt" style={{ fontSize: '14px', color: '#0DDFAB', flexShrink: 0 }} />
                                                                            <span style={{ fontSize: '13px', color: '#1d1d1d', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', flex: 1, minWidth: 0 }}>
                                                                                {lead.created_at ? moment(lead.created_at).format('D, MMMM') : (lead.createdAt ? moment(lead.createdAt).format('D, MMMM') : "-")}
                                                                            </span>
                                                                        </div>
                                                                    </div>

                                                                    {/* Owner */}
                                                                    <div style={{ width: COL.owner, flexShrink: 0, padding: '0 8px', overflow: 'hidden' }}>
                                                                        <AssigneField
                                                                            item={lead}
                                                                            assigne={allInitialData.users}
                                                                            Owner={lead.isHistoricalRow ? lead.display_owner : lead.owner}
                                                                            Services={updateLeadField}
                                                                            localUpdate={handleLocalUpdate}
                                                                            disabled={activeTimeFilter === 'previously_assigned'}
                                                                            avatarSize="18px"
                                                                            bgColor="#0DDFAB"
                                                                            textColor="#1d1d1d"
                                                                            styles={{ fontSize: '11px' }}
                                                                        />
                                                                    </div>

                                                                    {/* Type */}
                                                                    <div style={{ width: COL.type, flexShrink: 0, padding: '0 8px', overflow: 'hidden' }}>
                                                                        <span style={{ fontSize: '13px', color: '#1d1d1d', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                                                                            {allInitialData.relatedTo?.find(d => String(d.id || d._id) === String(lead.department_id))?.name || "-"}
                                                                        </span>
                                                                    </div>

                                                                    {/* Due Date */}
                                                                    <div style={{ width: COL.date, flexShrink: 0, padding: '0 8px', overflow: 'hidden' }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%', overflow: 'hidden' }}>
                                                                            <i className="uil uil-calendar-alt" style={{ fontSize: '14px', color: '#0DDFAB', flexShrink: 0 }} />
                                                                            <DateField
                                                                                item={lead}
                                                                                format="D, MMMM"
                                                                                defaultDate={lead.due_date}
                                                                                Services={updateLeadField}
                                                                                data={(newDate) => handleLocalUpdate(lead.isHistoricalRow ? lead.originalId : lead.id, { due_date: newDate })}
                                                                                styles={{ fontSize: '13px', color: '#1d1d1d', fontWeight: '500' }}
                                                                                suffixIcon={null}
                                                                                disabled={activeTimeFilter === 'previously_assigned'}
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    {/* Source */}
                                                                    <div style={{ width: COL.source, flexShrink: 0, padding: '0 8px', overflow: 'hidden' }}>
                                                                        {(() => {
                                                                            const src = allInitialData.sources?.find(s => String(s.id || s._id) === String(lead.lead_source));
                                                                            const srcName = src?.name || src?.source || src?.value || "-";
                                                                            return (
                                                                                <span style={{ fontSize: '13px', color: '#1d1d1d', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                                                                                    {srcName}
                                                                                </span>
                                                                            );
                                                                        })()}
                                                                    </div>

                                                                    {/* Stage */}
                                                                    <div style={{ width: COL.stage, flexShrink: 0, padding: '0 6px', overflow: 'hidden' }}>
                                                                        <span className="stage-badge" style={{
                                                                            background: cfg.bg, color: cfg.color,
                                                                            padding: "3px 10px",
                                                                            fontSize: "11px", fontWeight: "600",
                                                                            whiteSpace: "nowrap", display: "inline-block",
                                                                        }}>{cfg.label.toUpperCase()}</span>
                                                                    </div>

                                                                    {/* Actions */}
                                                                    <div style={{ width: COL.actions, flexShrink: 0, padding: '0 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                                                        {canEdit && (
                                                                            <>
                                                                                <style>{`
                                                                    .task-comments-popover {
                                                                        box-shadow: none !important;
                                                                    }
                                                                    .task-comments-popover .ant-popover-inner {
                                                                        border: 1px solid #F2F2F2 !important;
                                                                        border-radius: 5px !important;
                                                                        box-shadow: none !important;
                                                                        background: #fff !important;
                                                                    }
                                                                    .task-comments-popover .ant-popover-arrow {
                                                                        display: none !important;
                                                                        visibility: hidden !important;
                                                                        opacity: 0 !important;
                                                                    }
                                                                `}</style>
                                                                                <Popover
                                                                                    overlayClassName="task-comments-popover"
                                                                                    overlayStyle={{ boxShadow: 'none' }}
                                                                                    content={
                                                                                        <div style={{ width: '400px' }}>
                                                                                            <LeadComments leadId={lead.id} localNotes={commentLeadNotes} setLocalNotes={setCommentLeadNotes} mentionUsers={mentionUsers} currentUserId={currentUserId} companyId={companyId} />
                                                                                        </div>
                                                                                    }
                                                                                    title={null}
                                                                                    trigger="click"
                                                                                    open={openCommentLeadId === (lead.isHistoricalRow ? lead.originalId : lead.id)}
                                                                                    onOpenChange={(open) => {
                                                                                        if (open) handleOpenComments(lead);
                                                                                        else setOpenCommentLeadId(null);
                                                                                    }}
                                                                                    placement="bottomRight"
                                                                                    getPopupContainer={trigger => trigger.parentElement}
                                                                                    mask={false}
                                                                                >
                                                                                    <div
                                                                                        className="d-flex align-items-center gap-1 cursor-pointer"
                                                                                        style={{ color: openCommentLeadId === (lead.isHistoricalRow ? lead.originalId : lead.id) ? '#1d1d1d' : '#9ca3af' }}
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleOpenComments(lead);
                                                                                        }}
                                                                                    >
                                                                                        <span className="comment-svg-icon" style={{ width: '18px', height: '18px', mask: "url('/assets/images/comment.svg') no-repeat center / contain", WebkitMask: "url('/assets/images/comment.svg') no-repeat center / contain" }} />
                                                                                        {lead.lead_notes?.length > 0 && (
                                                                                            <span style={{ fontSize: '12px', color: '#1d1d1d', fontWeight: '600', lineHeight: 1 }}>
                                                                                                {lead.lead_notes?.length}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                </Popover>

                                                                                <div onClick={(e) => { e.stopPropagation(); handleEdit(lead.id); }} style={{ cursor: 'pointer', color: '#000', display: 'flex', alignItems: 'center' }}>
                                                                                    <i className="uil uil-pen" style={{ fontSize: '15px' }} />
                                                                                </div>

                                                                                <Popconfirm title="Delete this lead?" onConfirm={() => confirmDelete(lead.id)} okText="Yes" cancelText="No" placement="left">
                                                                                    <div style={{ color: '#000', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                                                        <i className="uil uil-trash" style={{ fontSize: '15px' }} />
                                                                                    </div>
                                                                                </Popconfirm>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}

                                                        <div id="leads-list-sentinel" style={{ height: '2px', margin: '10px 0' }}></div>

                                                        {isLazyLoading && (
                                                            <div style={{ padding: '0 16px' }}><Skeleton height={44} borderRadius={8} /></div>
                                                        )}

                                                        {visibleLeadsCount < filteredLeads.length && !isLazyLoading && (
                                                            <div className="text-center mt-3 mb-4">
                                                                <button className="btn btn-sm btn-outline-secondary px-4" onClick={handleShowMore} style={{ borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                                                                    Show More Leads
                                                                </button>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="todo-task-list w-100">
                                                        <div className="task_heading_column justify-content-center py-5">
                                                            <span className="text-muted">No matching leads found.</span>
                                                        </div>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <Modal
                                    title={
                                        <span>
                                            <i className="uil uil-redo" style={{ marginRight: 8, color: '#3b82f6' }} />
                                            Re-open Lead
                                        </span>
                                    }
                                    open={reopenModal}
                                    onOk={handleReopenConfirm}
                                    onCancel={() => setReopenModal(false)}
                                    okText="Re-open"
                                    okButtonProps={{ style: { background: '#3b82f6', borderColor: '#3b82f6' } }}
                                    confirmLoading={reopenLoading}
                                    centered
                                    width={420}
                                >
                                    <p style={{ color: '#64748b', margin: 0 }}>
                                        Re-open <strong>{reopenTarget?.title || `Lead #${reopenTarget?.id}`}</strong>?
                                        It will be moved back to <strong>New</strong> stage.
                                    </p>
                                </Modal>

                                {isModalOpen && (
                                    <div
                                        className="crm-lead-modal-overlay"
                                        onClick={() => setIsModalOpen(false)}
                                    >
                                        <CreateCrmLead
                                            editId={editLeadId}
                                            canEditLead={leads.find(l => l.id === editLeadId)?.can_edit_lead}
                                            initialData={allInitialData}
                                            onCancel={() => setIsModalOpen(false)}
                                            onSuccess={() => {
                                                setIsModalOpen(false);
                                                fetchLeads(true);
                                            }}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default CrmLeads;